// Contour fields: seeded gradient noise for terrain, and marching squares to trace its
// level lines. The hero's map and the homepage figures draw from here.

export type Field = (x: number, y: number) => number;

const TAU = Math.PI * 2;
const lerp = (a: number, b: number, q: number) => a + (b - a) * q;

// Gradient noise from a seed, so a field looks the same on every visit.
export function noise(seed: number): Field {
  let s = seed >>> 0;
  const random = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
  const perm = Array.from({ length: 256 }, (_, i) => i);
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [perm[i], perm[j]] = [perm[j], perm[i]];
  }
  const p = new Uint8Array(512);
  for (let i = 0; i < 512; i++) p[i] = perm[i & 255];
  const grad = (h: number, x: number, y: number) => {
    const a = ((h & 7) * TAU) / 8;
    return Math.cos(a) * x + Math.sin(a) * y;
  };
  const fade = (q: number) => q * q * q * (q * (q * 6 - 15) + 10);
  return (x, y) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const X = xi & 255;
    const Y = yi & 255;
    const fx = x - xi;
    const fy = y - yi;
    const u = fade(fx);
    const v = fade(fy);
    const a = p[X] + Y;
    const b = p[X + 1] + Y;
    return lerp(
      lerp(grad(p[a], fx, fy), grad(p[b], fx - 1, fy), u),
      lerp(grad(p[a + 1], fx, fy - 1), grad(p[b + 1], fx - 1, fy - 1), u),
      v,
    );
  };
}

// For each corner pattern of a cell, the pairs of edges a level line crosses
// (edges: 0 top, 1 right, 2 bottom, 3 left).
const SEGMENTS: (number[] | null)[] = [
  null, [3, 0], [0, 1], [3, 1], [1, 2], [3, 0, 1, 2], [0, 2], [3, 2],
  [2, 3], [0, 2], [0, 1, 2, 3], [1, 2], [1, 3], [0, 1], [3, 0], null,
];

export interface Grid {
  cols: number;
  rows: number;
  cell: number;
  values: Float32Array;
}

export function sample(f: Field, width: number, height: number, cell: number): Grid {
  const cols = Math.ceil(width / cell) + 1;
  const rows = Math.ceil(height / cell) + 1;
  const values = new Float32Array(cols * rows);
  for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) values[j * cols + i] = f(i * cell, j * cell);
  return { cols, rows, cell, values };
}

// Calls back with each segment of one level line, and the cell it crosses.
export function trace(
  grid: Grid,
  level: number,
  emit: (x1: number, y1: number, x2: number, y2: number, cellIndex: number) => void,
) {
  const { cols, rows, cell, values } = grid;
  for (let j = 0; j < rows - 1; j++) {
    for (let i = 0; i < cols - 1; i++) {
      const k = j * cols + i;
      const a = values[k];
      const b = values[k + 1];
      const c = values[k + cols + 1];
      const d = values[k + cols];
      const seg = SEGMENTS[(a > level ? 1 : 0) | (b > level ? 2 : 0) | (c > level ? 4 : 0) | (d > level ? 8 : 0)];
      if (!seg) continue;
      const x0 = i * cell;
      const y0 = j * cell;
      const ex = [x0 + (cell * (level - a)) / (b - a), x0 + cell, x0 + (cell * (level - d)) / (c - d), x0];
      const ey = [y0, y0 + (cell * (level - b)) / (c - b), y0 + cell, y0 + (cell * (level - a)) / (d - a)];
      for (let q = 0; q < seg.length; q += 2) emit(ex[seg[q]], ey[seg[q]], ex[seg[q + 1]], ey[seg[q + 1]], k);
    }
  }
}

// Strokes every level of a sampled field, one path per level, in the colour and width
// that `style` gives it.
export function strokeContours(
  ctx: CanvasRenderingContext2D,
  grid: Grid,
  levels: number[],
  style: (level: number, index: number) => [string, number],
) {
  levels.forEach((level, index) => {
    const [colour, width] = style(level, index);
    ctx.beginPath();
    trace(grid, level, (x1, y1, x2, y2) => {
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    });
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.stroke();
  });
}
